import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useDecidedApprovalsQuery, usePendingApprovals } from "./approvals";
import { useHubApi } from "./context";

const parcelKey = ["hub", "approval-parcels"] as const;
const preferenceKey = ["hub", "me", "approval-notifications"] as const;
const refreshOptions = { refetchInterval: 5_000, refetchOnWindowFocus: true, staleTime: 0 };

export function useApprovalParcels(status: "outstanding" | "decided", enabled: boolean) {
  const api = useHubApi();
  return useInfiniteQuery({
    queryKey: [...parcelKey, "list", status],
    queryFn: ({ pageParam }) => api.approvalParcels.list(status, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    ...refreshOptions,
  });
}

export function useOutstandingApprovals(enabled: boolean) {
  const pending = usePendingApprovals({ enabled });
  const confirmations = useDecidedApprovalsQuery(
    { enabled },
    pending.confirmations.map((approval) => approval.confirmationId),
  );
  const parcels = useApprovalParcels("outstanding", enabled);
  const { fetchNextPage, hasNextPage, isFetching, isError } = parcels;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetching && !isError)
      void fetchNextPage({ cancelRefetch: false });
  }, [enabled, fetchNextPage, hasNextPage, isFetching, isError]);
  const count =
    enabled &&
    confirmations.isSuccess &&
    !confirmations.isPlaceholderData &&
    parcels.isSuccess &&
    !hasNextPage
      ? confirmations.data.pendingConfirmationIds.length +
        parcels.data.pages.reduce(
          (total, page) =>
            total + page.parcels.filter((parcel) => parcel.state === "pending").length,
          0,
        )
      : undefined;
  return { pending, confirmations, parcels, count };
}

export function useOutstandingApprovalCount(enabled: boolean) {
  return useOutstandingApprovals(enabled).count;
}

export function useApprovalParcel(id: string) {
  const api = useHubApi();
  return useQuery({
    queryKey: [...parcelKey, id],
    queryFn: () => api.approvalParcels.get(id),
    ...refreshOptions,
  });
}

export function useParcelDecision(id: string) {
  const api = useHubApi();
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (verdict: "approved" | "rejected") => api.approvalParcels.decide(id, verdict),
    onSuccess: (result) => {
      cache.setQueryData([...parcelKey, id], result.parcel);
      void cache.invalidateQueries({ queryKey: parcelKey });
    },
  });
}

export function useApprovalPreferences() {
  const api = useHubApi();
  return useQuery({
    queryKey: preferenceKey,
    queryFn: () => api.approvalParcels.preferences(),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useSetApprovalPreference() {
  const api = useHubApi();
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, enabled }: { connectionId: string; enabled: boolean }) =>
      api.approvalParcels.setPreference(connectionId, enabled),
    onSuccess: (result) => cache.setQueryData(preferenceKey, result),
  });
}
